import {EventEmitter} from 'events';
import every from 'lodash/every';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import get from 'lodash/get';
import has from 'lodash/has';
import tsDate from '@techsee/techsee-date';
import {senderTypes} from '@techsee/techsee-common/lib/constants/resource.constants';
import utils from '@techsee/techsee-common/lib/utils';
import {RestApiService} from '@techsee/techsee-client-infra/lib/services/RestApiService';
import {SIGNED_URL_EXPIRATION_BUFFER} from '../ts-chat-api/ts-chat-api.settings';
import {
    ISharingService,
    ISharingParams,
    ISignedData,
    IImageUploadCallback,
    ISharingModeActions,
    ActionTypes
} from './contracts';
import {IHistoryService} from '@techsee/techsee-client-infra/lib/services/HistoryService';
import {IRoomService, IUploadToClipboard} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {uuidv4} from '@techsee/techsee-common/lib/core/uuid';

const supportedTypes = {
    url: 'url',
    dataUrl: 'dataUrl',
    text: 'text'
};

const parseExpiration = (dateString: string, format: string, duration: number): number =>
    tsDate.utc(dateString, format).add(duration, 'seconds').unix();

function _getResourceExpireTime(url: string): number {
    //@ts-ignore
    const expires = utils.getResourceExpireTime(url, parseExpiration);
    return expires - SIGNED_URL_EXPIRATION_BUFFER;
}

export class TsSharingService extends EventEmitter implements ISharingService {
    private roomId?: string;
    private isIE: boolean;

    constructor(
        private historyService: IHistoryService,
        private roomService: IRoomService,
        private _chatApi: any,
        private _tsCanvasAnnotate: any,
        private _environmentService: any,
        private _eventLog: any,
        private _accountData: any,
        private _dashboardState?: any
    ) {
        super();
        this.isIE = this._environmentService.isIE11();
    }

    init(roomId: string): void {
        this.roomId = roomId;
    }

    enableCopyToClipboard() {
        return this._accountData.protectedSettings.enableCopyToClipboard;
    }

    enableEmailImageSharing() {
        return this._accountData.protectedSettings.emailImageSharing;
    }

    enableLocalImageSaving() {
        return this._accountData.protectedSettings.localImageSaving;
    }

    getShareModeActions(): ISharingModeActions[] {
        return [
            {
                enable: this.enableCopyToClipboard(),
                icons: {
                    enable: 'new-interface-assets/copy-to-clipboard.png',
                    disable: 'new-interface-assets/copy-to-clipboard-dis.png'
                },
                type: ActionTypes.copyToClipboard,
                alt: 'Copy to clipboard'
            },
            {
                enable: this.enableEmailImageSharing(),
                icons: {
                    enable: 'new-interface-assets/send-email.png',
                    disable: 'new-interface-assets/send-email-dis.png'
                },
                type: ActionTypes.sendByEmail,
                alt: 'Send by email'
            },
            {
                enable: this.enableLocalImageSaving(),
                icons: {
                    enable: 'new-interface-assets/download.png',
                    disable: 'new-interface-assets/download.png'
                },
                type: ActionTypes.download,
                alt: 'Download'
            },
            {
                enable: true,
                icons: {
                    enable: 'new-interface-assets/share.png',
                    disable: 'new-interface-assets/share.png'
                },
                type: ActionTypes.share,
                alt: 'Share'
            }
        ];
    }

    private get accountSettings() {
        return this._chatApi.accountSettings || {};
    }

    updateHistoryService(historyService: IHistoryService): void {
        this.historyService = historyService;
    }

    updateRoomService(roomService: IRoomService): void {
        this.roomService = roomService;
    }

    private _validateParams(params: ISharingParams | ISharingParams[], requiresSignedData = true): ISharingParams[] {
        const isArray = Array.isArray(params);
        let validatedParams = cloneDeep(params);

        if (!isArray) {
            validatedParams = [validatedParams as ISharingParams];
        }

        this._validateInput(validatedParams as ISharingParams[], requiresSignedData);
        return validatedParams as ISharingParams[];
    }

    async copyToClipboard(params: ISharingParams | ISharingParams[], purpose = 'COPY_TO_CLIPBOARD'): Promise<void> {
        const validatedParams = this._validateParams(params);
        const promises: Promise<any>[] = [];
        const fileNames: string[] = [];

        forEach(validatedParams, (param) => {
            const {blobImage, fileName} = param.signedData!;
            promises.push(this.uploadClipboardImage(blobImage, fileName, param.signedData));
            fileNames.push(fileName);
        });

        try {
            await Promise.all(promises);

            try {
                this._executeCopyToClipboard(validatedParams);

                if (validatedParams.length > 1) {
                    this._eventLog.shareMultipleImagesCopyToClipboardSuccess({purpose, fileNames});
                } else {
                    this._eventLog.shareImageCopyToClipboardSuccess({purpose, fileNames});
                }
            } catch (err) {
                if (validatedParams.length > 1) {
                    this._eventLog.shareMultipleImagesCopyToClipboardCopyFailure({
                        purpose,
                        fileNames,
                        err
                    });
                } else {
                    this._eventLog.shareImageCopyToClipboardCopyFailure({purpose, fileNames, err});
                }
            }
        } catch (err) {
            this._eventLog.shareImageCopyToClipboardUploadFailure({purpose, fileNames, err});
            throw err;
        }
    }

    copyTextToClipboard(textParam: ISharingParams, purpose = 'COPY_TEXT_TO_CLIPBOARD'): void {
        try {
            this._validateParams(textParam, false);

            const textCopied = this._executeCopyTextToClipboard(textParam);

            if (textCopied) {
                this._eventLog.shareCopyTextToClipboardSuccess({purpose, textParam});
            } else {
                this._eventLog.shareCopyTextToClipboardFailure({purpose, textParam});
            }
        } catch (err) {
            this._eventLog.shareCopyTextToClipboardFailure({purpose, textParam, err});
        }
    }

    async uploadClipboardImage(img: Blob | string, fileName: string, presignedRequest?: any): Promise<any> {
        if (!this.roomId) {
            return Promise.reject();
        }

        this._eventLog.uploadingClipboardImage();

        return new Promise((resolve, reject) => {
            this.uploadImageS3(
                img,
                fileName,
                true,
                (id, payload) => {
                    if (presignedRequest && presignedRequest.signedRequest) {
                        const url = presignedRequest.signedRequest;

                        if (Math.floor(Date.now() / 1000) < _getResourceExpireTime(url)) {
                            return Promise.resolve({data: presignedRequest});
                        }
                    }

                    return this.roomService.uploadClipboard(id, payload.data as IUploadToClipboard);
                },
                resolve,
                reject
            );
        });
    }

    uploadRoomImage(
        img: Blob | string,
        options: any,
        cbSuccess: (url: string) => void,
        cbErr: (err?: any) => void
    ): void {
        if (!this.roomId) {
            return cbErr();
        }

        const roomUploadCb: IImageUploadCallback = (id, payload) => {
            if (has(options, 'isTemp')) {
                payload.data.isTemp = options.isTemp;
            }

            return this.roomService.createUrlForUpload(id, payload.data);
        };
        this.uploadImageS3(img, get(options, 'fileName') || null, true, roomUploadCb, cbSuccess, cbErr);
    }

    uploadAutoCollectedImage(img: Blob | string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.roomId) {
                return reject();
            }

            const roomUploadCb: IImageUploadCallback = (id, payload) => {
                payload.data.isAuto = true;
                return this.roomService.createUrlForUpload(id, payload.data);
            };

            this.uploadImageS3(
                img,
                null,
                false,
                roomUploadCb,
                () => resolve(),
                () => reject()
            );
        });
    }

    uploadTriggeredCollectedImage(img: Blob | string, source: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.roomId) {
                return reject();
            }

            const roomUploadCb: IImageUploadCallback = (id, payload) => {
                payload.data.triggeredSource = source;
                return this.roomService.createUrlForUpload(id, payload.data);
            };

            this.uploadImageS3(
                img,
                null,
                false,
                roomUploadCb,
                () => resolve(),
                () => reject()
            );
        });
    }

    uploadImageS3(
        img: Blob | string,
        fileName: string | null,
        loggingEnabled: boolean,
        cbSign: IImageUploadCallback,
        cbSuccess: (url: string) => void,
        cbErr: (err: any) => void
    ): void {
        this._generateImageFileName(img, fileName, (blob, imgName) => {
            if (typeof img === 'string') {
                URL.revokeObjectURL(img);
            }

            const mediaFileSizeInKB = this._convertToSizeString(blob.size);

            if (loggingEnabled) {
                this._eventLog.imageUploadStarted({mediaFileName: imgName, ImageSize: mediaFileSizeInKB});
            }

            const imageUploadTimeout = get(this.accountSettings, 'imageUploadTimeoutSeconds');

            cbSign(this.roomId!, {
                data: {
                    fileName: imgName,
                    fileType: blob.type,
                    fileSize: blob.size
                }
            })
                .then((response) => {
                    const url = get(response, 'data.signedRequest', response?.signedRequest);
                    const publicUrl = get(response, 'data.publicUrl', response?.publicUrl);

                    const http = new RestApiService('', undefined, {
                        'Content-Type': blob.type
                    });

                    http.put(url, blob, {
                        timeout: imageUploadTimeout * 1000,
                        responseType: 'text'
                    })
                        .then((resp) => {
                            if (loggingEnabled) {
                                this._eventLog.imageUploadSuccess({
                                    mediaFileName: imgName,
                                    ImageSize: mediaFileSizeInKB,
                                    status: resp?.status ?? ''
                                });
                            }

                            if (cbSuccess) {
                                return cbSuccess(publicUrl);
                            }
                        })
                        .catch((err) => {
                            if (loggingEnabled) {
                                this._eventLog.imageUploadFailed({
                                    url: url,
                                    status: err?.status ?? '',
                                    error: err,
                                    failurePhase: 'Uploading to S3',
                                    mediaFileName: imgName
                                });
                            }

                            cbErr(err);
                        });
                })
                .catch((err) => {
                    if (loggingEnabled) {
                        this._eventLog.imageUploadFailed({
                            err,
                            failurePhase: 'Image Signing',
                            mediaFileName: imgName
                        });
                    }

                    cbErr(err);
                });
        });
    }

    async generateImageFileName(img: Blob | string, fileName?: string): Promise<{blob: Blob; imgFileName: string}> {
        return new Promise((resolve) => {
            this._generateImageFileName(img, fileName as string, (blob, imgFileName) => resolve({blob, imgFileName}));
        });
    }

    private _generateImageFileName(
        img: Blob | string,
        fileName: string | null,
        cb: (blob: Blob, imgFileName: string) => void
    ): void {
        this.imageToBlob(img, (blob) => {
            const ext = blob.type.match(/image\/(.*)/i);
            const imgFileName = fileName || `${Date.now()}-dashboard${ext && ext[1] ? `.${ext[1]}` : ''}`;
            return cb(blob, imgFileName);
        });
    }

    imageToBlob(img: Blob | string, callback: (blob: Blob) => void): void {
        if (img instanceof Blob) {
            return callback(img);
        }

        const xhr = new XMLHttpRequest();
        xhr.open('GET', img, true);
        xhr.responseType = 'blob';
        xhr.onload = () => {
            if (xhr.status === 200 || xhr.status === 0) {
                return callback(xhr.response);
            }
        };
        xhr.send();
    }

    async sendByEmail(mailTrigger: string, params: ISharingParams[]): Promise<void> {
        await this.copyToClipboard(params, 'SEND_BY_EMAIL');
        window.open(mailTrigger);
    }

    async sendImagesToClient(params: ISharingParams[]): Promise<void> {
        for (const param of params) {
            const {blobImage, fileName} = param.signedData!;
            const options = {fileName};

            await new Promise<void>((resolve, reject) => {
                this._eventLog.dashboardSendingImage({library: true, mediaFileName: fileName});
                this.uploadRoomImage(
                    blobImage,
                    options,
                    (url) => {
                        this._chatApi
                            .sendImage(url, {
                                sharedBy: senderTypes.DASHBOARD,
                                mediaFileName: fileName
                            })
                            .then(() => resolve())
                            .catch((err: any) => reject(err));
                    },
                    (err) => reject(err)
                );
            });
        }
    }

    async download(params: ISharingParams): Promise<void> {
        this._validateInput([params], false);

        const fileName = params.fileName || 'img.jpg';
        const saveImage = (base64Image: string) => {
            if (this.isIE) {
                const blob = this._tsCanvasAnnotate.dataUrlToBlob(base64Image);
                (window.navigator as any).msSaveBlob(blob, fileName);
            } else {
                const dataUrl = params.useOriginalImage
                    ? this._tsCanvasAnnotate.dataUrlToObjectUrl(base64Image)
                    : base64Image;
                const downloadElement = document.createElement('a');

                downloadElement.setAttribute('download', fileName);
                downloadElement.href = dataUrl;

                document.body.appendChild(downloadElement);
                downloadElement.click();
                document.body.removeChild(downloadElement);
            }
        };

        if (params.type === supportedTypes.url) {
            return new Promise<void>((resolve, reject) => {
                getImageAsBase64(params.url!, params._id, this.historyService.updateResource)
                    .then((base64image) => {
                        try {
                            saveImage(base64image);
                        } catch (err) {
                            this._eventLog.shareImageSaveLocallySaveFailure({params, err});
                            return reject('save');
                        }
                        this._eventLog.shareImageSaveLocallySuccess(params);
                        resolve();
                    })
                    .catch((err) => {
                        this._eventLog.shareImageSaveLocallyDownloadFailure({params, err});
                        reject('download');
                    });
            });
        }

        if (params.type === supportedTypes.dataUrl) {
            try {
                saveImage(this._tsCanvasAnnotate.getDataURL({useOriginalImage: params.useOriginalImage}));
            } catch (err) {
                this._eventLog.shareImageSaveLocallySaveFailure({params, err});
                return Promise.reject('save');
            }

            this._eventLog.shareImageSaveLocallySuccess(params);
            return Promise.resolve();
        }

        return Promise.reject('unexpected');
    }

    async getSignedDataForImageSharing(params: ISharingParams): Promise<ISignedData> {
        this._validateInput([params], false);

        if (!this.roomId) {
            return Promise.reject('Room id not exists');
        }

        const fileName = `clipboard-copy-${uuidv4()}-${Date.now()}.jpeg`;
        const payload = {
            data: {
                fileName: fileName,
                fileType: 'image/jpeg',
                shorten: true
            }
        };

        const promises: Promise<any>[] = [];

        // Add better error handling for the room service call
        promises.push(
            this.roomService
                .uploadClipboard(this.roomId, payload.data as IUploadToClipboard)
                .then((result: any) => {
                    if (!result?.data) {
                        throw new Error('Room service returned invalid response');
                    }
                    return result.data;
                })
                .catch((error: any) => {
                    console.error('Room service uploadClipboard error:', error);
                    throw error;
                })
        );

        if (params.type === supportedTypes.dataUrl) {
            const dataUrl = this._tsCanvasAnnotate.getDataURL({useOriginalImage: params.useOriginalImage});
            promises.push(this._tsCanvasAnnotate.dataUrlToBlob(dataUrl));
        }

        if (params.type === supportedTypes.url) {
            promises.push(
                getImageAsBase64(params.url!, params._id, this.historyService.updateResource).then((base64Image) =>
                    this._tsCanvasAnnotate.dataUrlToBlob(base64Image)
                )
            );
        }

        try {
            const results = await Promise.all(promises);
            const signedData = results[0];
            const blobImage = results[1];

            if (!signedData) {
                throw new Error('Failed to get signed data from room service');
            }

            if (blobImage) {
                signedData.blobImage = blobImage;
            }

            return signedData;
        } catch (err) {
            if (!params.tryAgain) {
                return this.getSignedDataForImageSharing({...params, tryAgain: true});
            } else {
                this._eventLog.shareImageFetchSignedDataFailure({params, err});
                throw 'signing';
            }
        }
    }

    private _validateInput(params: ISharingParams[], requiresSignedData: boolean): void {
        const isSupportedTypes = every(params, (param) => !includes(supportedTypes, param.type));

        if (isSupportedTypes) {
            throw `provided 'type' is not supported. Supported types are: ${Object.keys(supportedTypes).join()}`;
        }

        if (requiresSignedData && !every(params, (param) => param.signedData)) {
            throw "'signedData' should be provided as part of params.";
        }

        if (!every(params, (param) => param.type !== 'url' || typeof param.url !== 'undefined')) {
            throw "If type is 'url', url should be provided.";
        }

        if (!every(params, (param) => param.type !== 'dataUrl' || typeof param.useOriginalImage !== 'undefined')) {
            throw "If type is 'dataUrl', useOriginalImage should be provided.";
        }

        if (!every(params, (param) => param.type !== 'text' || typeof param.data === 'string')) {
            throw "If type is 'text', only text params should be provided.";
        }
    }

    private _executeCopyToClipboard(params: ISharingParams[]): void {
        const imageElementsArray: HTMLImageElement[] = [];
        let index = 0;

        forEach(params, () => {
            imageElementsArray.push(new Image());
        });

        const selection = window.getSelection ? window.getSelection() : (document as any).selection;
        const range = document.createRange();

        forEach(imageElementsArray, (imageElement) => {
            imageElement.src = params[index++].signedData!.publicUrl;
        });

        if (selection?.empty) {
            selection.empty();
        } else if (selection?.removeAllRanges) {
            selection.removeAllRanges();
        }

        forEach(imageElementsArray, (imageElement) => {
            document.body.appendChild(imageElement);
        });

        range.setStartBefore(imageElementsArray[0]);
        range.setEndAfter(last(imageElementsArray)!);

        selection?.addRange(range);

        document.execCommand('copy');

        forEach(imageElementsArray, (imageElement) => {
            document.body.removeChild(imageElement);
        });
    }

    private _executeCopyTextToClipboard(itemToCopy: ISharingParams): boolean {
        const dummyTextAreaElement = document.createElement('textarea');

        dummyTextAreaElement.style.position = 'fixed';
        dummyTextAreaElement.style.opacity = '0';

        dummyTextAreaElement.value = itemToCopy.data!;
        dummyTextAreaElement.textContent = itemToCopy.data!;

        document.body.appendChild(dummyTextAreaElement);

        dummyTextAreaElement.focus();
        dummyTextAreaElement.select();

        const copySucceeded = document.execCommand('copy');

        document.removeChild(dummyTextAreaElement);

        return copySucceeded;
    }

    imageAsBase64(url: string, options?: any): Promise<string> {
        return getImageAsBase64(url, undefined, undefined, options);
    }

    private _convertToSizeString(size: number): string {
        // This is a placeholder - you'll need to implement the actual conversion logic
        // or import the TsUtils class that contains this method
        return `${Math.round(size / 1024)}KB`;
    }
}

function getImageAsBase64(url: string, resourceId?: string, resignUrl?: any, options: any = {}): Promise<string> {
    const toDataUrl = (url: string): Promise<string> =>
        new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = function () {
                const reader = new FileReader();

                reader.onloadend = function () {
                    resolve(reader.result as string);
                };

                reader.readAsDataURL(xhr.response);
            };

            xhr.onerror = (error) => reject(error);
            xhr.open('GET', url);
            xhr.setRequestHeader('Cache-Control', 'no-cache');
            xhr.responseType = 'blob';
            xhr.send();
        });

    const loadImageAndConvert = (url: string): Promise<string> =>
        new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = function () {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d')!;

                canvas.width = (options && options.width) || img.width;
                canvas.height = (options && options.height) || img.height;

                if (options && options.width) {
                    ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
                } else {
                    ctx.drawImage(img, 0, 0);
                }

                resolve(canvas.toDataURL('image/jpeg'));
            };

            img.crossOrigin = 'Anonymous';
            img.src = url;
            img.onerror = reject;
        });

    if (options && options.width) {
        return loadImageAndConvert(url).catch((err) => {
            if (!resignUrl) {
                throw err;
            }
            return resignUrl(resourceId).then((resigned: any) => toDataUrl(resigned.data.url));
        });
    }

    return toDataUrl(url).catch((err) => {
        if (!resignUrl) {
            throw err;
        }
        return resignUrl(resourceId).then((resigned: any) => toDataUrl(resigned.data.url));
    });
}
